import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';

import { apiErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { requestCode, verifyCode } from '@/services/courier';

const LAST_EMAIL = 'resstro-courier-last-email';

export default function LoginPage() {
  const setTokens = useAuthStore((s) => s.setTokens);
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState(() => localStorage.getItem(LAST_EMAIL) ?? '');
  const [code, setCode] = useState('');
  const [nextAt, setNextAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step !== 'code') return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [step]);

  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await requestCode(email.trim());
      localStorage.setItem(LAST_EMAIL, email.trim());
      setNextAt(new Date(res.nextAt).getTime());
      setNow(Date.now());
      setStep('code');
      setCode('');
      window.setTimeout(() => codeRef.current?.focus(), 50);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (value: string) => {
    setBusy(true);
    setError(null);
    try {
      const t = await verifyCode(email.trim(), value);
      setTokens(t.access_token, t.refresh_token);
    } catch (err) {
      setError(apiErrorMessage(err));
      setCode('');
      codeRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const wait = Math.max(0, Math.ceil((nextAt - now) / 1000));

  return (
    <div className="pt-safe pb-safe flex min-h-dvh flex-col bg-white">
      <div className="mx-auto flex w-full max-w-sm flex-1 flex-col px-6 pt-[12vh]">
        <img src="/pwa-192.png" alt="" width={56} height={56} className="mb-6 h-14 w-14 rounded-2xl" />
        {step === 'email' ? (
          <form onSubmit={send} className="flex flex-1 flex-col">
            <h1 className="text-2xl font-semibold tracking-tight text-ink">Вход для курьера</h1>
            <p className="mt-2 text-[15px] text-ink/55">Почта, которую указал руководитель. Пришлём на неё код.</p>
            <input
              className="input mt-8"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@mail.ru"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              required
            />
            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
            <button type="submit" className="btn-primary mt-4 w-full" disabled={busy || !email.trim()}>
              {busy && <Loader2 size={18} className="animate-spin" />}
              Получить код
            </button>
          </form>
        ) : (
          <div className="flex flex-1 flex-col">
            <button type="button" className="-ml-2 mb-4 inline-flex w-fit items-center gap-1 rounded-lg px-2 py-1 text-sm text-ink/55" onClick={() => setStep('email')}>
              <ArrowLeft size={16} /> {email}
            </button>
            <h1 className="text-2xl font-semibold tracking-tight text-ink">Код из письма</h1>
            <p className="mt-2 text-[15px] text-ink/55">Шесть цифр. Письмо может прийти через минуту — проверьте «Спам».</p>
            <input
              ref={codeRef}
              className="input mt-8 text-center text-2xl font-semibold tracking-[0.5em] tabular-nums"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              disabled={busy}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                setCode(v);
                if (v.length === 6) void verify(v);
              }}
              aria-label="Код из письма"
            />
            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
            {busy && (
              <p className="mt-3 inline-flex items-center gap-2 text-sm text-ink/50">
                <Loader2 size={15} className="animate-spin" /> Проверяем…
              </p>
            )}
            <button type="button" className="btn-ghost mt-4 w-full" onClick={() => send()} disabled={busy || wait > 0}>
              {wait > 0 ? `Отправить ещё раз через ${wait} с` : 'Отправить код ещё раз'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
